import React, {
  useState,
  useRef,
  useMemo,
  type PropsWithChildren,
  useCallback,
} from 'react';
import useId from '@mc/hooks/useId';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import TabContext from './TabContext';
import stylesheet from './Tabs.css';

export type TabsProps = PropsWithChildren<{
  /** Default index number. */
  defaultIndex?: number;
  /** Current index number. */
  index?: number;
  /** Tabs require manual selection, after focus on*/
  isManual?: boolean;
  /** Triggers when the Tab value has changed. */
  onChange?: VoidFunction;
  /** Orientation of the tabs, defaults to horizontal */
  orientation?: 'horizontal' | 'vertical';
  /** Option to render medium or large tabs, small is deprecated and has to be removed when removing flag */
  size?: 'large' | 'medium' | 'small';
  /** Option to render chips or underline tabs */
  variant?: 'chips' | 'underline';
}>;

const Tabs = function Tabs({
  size = 'large',
  children,
  index,
  defaultIndex,
  onChange,
  orientation = 'horizontal',
  isManual = false,
  variant = 'underline',
}: TabsProps) {
  const { current: isControlled } = useRef(index !== undefined);
  const [uncontrolledIndex, setUncontrolledIndex] = useState(defaultIndex || 0);
  const [focusIndex, setFocusIndex] = useState(defaultIndex || 0);
  const [showOutline, setShowOutline] = useState(false);
  const [disabledMap, setDisabledMap] = useState<Record<number, boolean>>({});
  const [tabsCount, setTabsCount] = useState<number>(0);
  const id = useId();

  const currentIndex = isControlled ? index : uncontrolledIndex;
  const setCurrentIndex = isControlled ? onChange : setUncontrolledIndex;
  const defaultVariant = orientation === 'vertical' ? 'chips' : variant;

  const isNewFeaturesEnabled = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const isDisabled = useCallback(
    (idx: number) => {
      return disabledMap[idx] || false;
    },
    [disabledMap],
  );

  const moveToIndex = useCallback(
    (idx: number, relative: boolean = false) => {
      const newIdx = relative
        ? (focusIndex + idx + tabsCount) % tabsCount
        : idx % tabsCount;
      // Focus index should be updated irrespective of any condition
      setFocusIndex(newIdx);
      if (setCurrentIndex && disabledMap[newIdx] !== true && !isManual) {
        // Current index should be updated only when the tab navigating
        // to is not disabled and mode is not manual
        setCurrentIndex(newIdx);
      }
      if (isManual) {
        setShowOutline(true);
      }
    },
    [
      isManual,
      setCurrentIndex,
      setFocusIndex,
      focusIndex,
      tabsCount,
      disabledMap,
    ],
  );

  const contextProps = useMemo(
    () => ({
      id,
      currentIndex,
      setCurrentIndex,
      size,
      focusIndex,
      orientation,
      setFocusIndex,
      showOutline,
      setShowOutline,
      isManual,
      variant: defaultVariant,
      updateDisabledMap: setDisabledMap,
      isDisabled,
      setTabsCount,
      moveToIndex,
    }),
    [
      id,
      currentIndex,
      setCurrentIndex,
      size,
      focusIndex,
      orientation,
      showOutline,
      isManual,
      defaultVariant,
      isDisabled,
      setTabsCount,
      moveToIndex,
    ],
  );

  return isNewFeaturesEnabled ? (
    <div className={isNewFeaturesEnabled ? stylesheet[orientation] : null}>
      <TabContext.Provider value={contextProps}>{children}</TabContext.Provider>
    </div>
  ) : (
    <TabContext.Provider value={contextProps}>{children}</TabContext.Provider>
  );
};

export default Tabs;
